import DeviceInfo from 'APP/Helpers/DeviceInfo'
import RNCallKeep, { type EventsPayload as CallkeepEventsPayload } from 'react-native-callkeep'
import { delay, race, type SagaGenerator } from 'typed-redux-saga'
import { call, getContext, put, select } from 'typed-redux-saga'
import { videoSessionActions } from '../VideoSession'
import { logger } from 'APP/Lib/Datadog'
import { waitForAppHydrated } from 'APP/Store/VideoSession/sagas'
import { waitForNavigationReady } from 'APP/Sagas/StartupSagas'
import { selectNativeCall } from 'APP/Store/NativeCallIOS/selectors'
import { isIOS } from 'APP/Helpers/checkPlatform'
import type { PayloadAction } from '@reduxjs/toolkit'
import type {
  NativeCallDeclinedPayload,
  NativeCallDisplayedPayload,
  EndNativeCallPayload,
} from 'APP/Store/NativeCallIOS/types'
import { nativeCallIOSActions } from 'APP/Store/NativeCallIOS/slice'
import Analytics from 'APP/Services/Analytics'

// Only physical iOS device can use native call
export function* canUseNativeCall() {
  return !DeviceInfo.isEmulatorSync() && isIOS()
}

function* waitForNativeCallSessionId() {
  let { sessionId } = yield* select(selectNativeCall)
  const startTime = Date.now()

  while (!sessionId) {
    yield* delay(50)
    sessionId = (yield* select(selectNativeCall)).sessionId
  }

  const endTime = Date.now()
  logger.info('[iOS] sessionId found in native slice, continuing', {
    timeElapsed: endTime - startTime,
  })

  return sessionId
}

export function* waitForNativeCallSessionIdWithTimeout(
  /* The maximum amount of time to block for, before we time out. Defaults to 5000ms. */
  timeoutInMs: number = 5000
): SagaGenerator<string | null> {
  const { sessionId } = yield* race({
    sessionId: call(waitForNativeCallSessionId),
    timedOut: delay(timeoutInMs),
  })

  if (!sessionId) {
    logger.warn('[iOS] Timed out while waiting for sessionId in native slice', {
      timeoutInMs,
    })
    return null
  }

  return sessionId
}

export function* nativeCallAccepted() {
  yield* call(waitForAppHydrated)
  yield* call(waitForNavigationReady)

  logger.info('nativeCallAccepted saga started')

  const onDemandSessionId = yield* call(waitForNativeCallSessionIdWithTimeout)

  if (!onDemandSessionId) {
    // Don't do anything, if the user is logged into the app they will be able
    // to accept from the in-app modal here, it will be a degradation but
    // this really should not happen - if it does, it looks like a glitch
    // where you need to double-accept.
    logger.error('[iOS] Could not accept call from native notification')
    return
  }

  Analytics.trackEvent('video_call_accepted', {
    session_id: onDemandSessionId,
    source: 'native_call_ios',
  })

  logger.info('Allowing patient to join call after accepting iOS native call notification')

  yield* put(
    videoSessionActions.patientAcceptedCall({
      lastCallAcceptedAt: Date.now(),
      onDemandSessionId,
    })
  )
}

export function* nativeCallDeclined(action: PayloadAction<NativeCallDeclinedPayload>) {
  const declinedCallUUID = action.payload.callUUID
  const {
    callUUID: currentCallUUID,
    sessionId: onDemandSessionId,
    accepted: isActiveCall,
  } = yield* select(selectNativeCall)

  logger.info('nativeCallDeclined saga started', {
    declinedCallUUID,
    currentCallUUID,
    onDemandSessionId,
  })

  if (declinedCallUUID !== currentCallUUID) {
    logger.info(
      'nativeCallDeclined was called with a callUUID not matching the current callUUID. Will not decline the call, early exiting.',
      {
        declinedCallUUID,
        currentCallUUID,
        onDemandSessionId,
      }
    )
    return
  }

  if (isActiveCall) {
    Analytics.trackEvent('video_call_ended', {
      session_id: onDemandSessionId,
      source: 'native_call_ios',
    })
  } else {
    Analytics.trackEvent('video_call_declined', {
      session_id: onDemandSessionId,
      source: 'native_call_ios',
    })
  }

  yield* put(videoSessionActions.patientDeclinedCall({ onDemandSessionId }))
}

export function* callAccepted() {
  const shouldUseNativeCall = yield* call(canUseNativeCall)

  if (!shouldUseNativeCall) {
    return
  }

  const { callUUID, accepted } = yield* select(selectNativeCall)

  logger.info('callAccepted saga started', { callUUID, accepted })

  if (!callUUID || accepted) {
    return
  }

  logger.info('Accepting iOS native call with callUUID', { callUUID })
  RNCallKeep.answerIncomingCall(callUUID)
}

export function* endNativeCall(action: PayloadAction<EndNativeCallPayload>) {
  const { sessionId: storeSessionId, callUUID } = yield* select(selectNativeCall)
  const { onDemandSessionId } = action.payload

  logger.info('endNativeCall saga started', { onDemandSessionId, storeSessionId, callUUID })

  const shouldUseNativeCall = yield* call(canUseNativeCall)
  if (!shouldUseNativeCall) {
    return
  }

  if (onDemandSessionId && storeSessionId && onDemandSessionId !== storeSessionId) {
    logger.warn(
      'Cannot end native iOS call when onDemandSessionId does not match store value, exiting early',
      {
        onDemandSessionId,
        storeSessionId,
      }
    )
    return
  }

  if (callUUID) {
    RNCallKeep.endCall(callUUID)
  }

  yield* put(nativeCallIOSActions.resetState())
}

// Maps the RNCallKeep didDisplayIncomingCall event to the nativeCallDisplayed payload. sessionId
// is attached natively in reportNewIncomingCall; callUUID comes from the event itself.
function parseDisplayEvent(
  event: CallkeepEventsPayload['didDisplayIncomingCall']
): NativeCallDisplayedPayload | null {
  const { payload, ...scrubbedEvent } = event
  const { sessionId } = (payload ?? {}) as { sessionId?: string }
  const { callUUID } = scrubbedEvent

  if (!sessionId) {
    logger.error('Received RNCallKeep event with invalid payload', {
      sessionId,
      callUUID,
    })
    return null
  }

  return { sessionId, callUUID }
}

// Binds the RNCallKeep answer/decline/display listeners from the root saga so they are active as
// soon as the store boots — including a headless VoIP-push launch with no UI rendered.
// getInitialEvents drains anything RNCallKeep buffered before the listeners attached. dispatch
// (not put) is required: the addEventListener callbacks fire outside the saga's effect flow.
export function* attachNativeCallListeners() {
  if (!(yield* call(canUseNativeCall))) {
    return
  }

  // Drain only after hydration so rehydration can't clobber the dispatched call state.
  yield* call(waitForAppHydrated)

  const { dispatch } = yield* getContext<{ dispatch: (action: unknown) => void }>('store')

  const initialEvents = yield* call(() => RNCallKeep.getInitialEvents())
  logger.info('attachNativeCallListeners draining initial events', {
    count: initialEvents?.length ?? 0,
    names: initialEvents?.map((e) => e.name),
  })
  for (const event of initialEvents ?? []) {
    if (event.name === 'RNCallKeepPerformAnswerCallAction') {
      dispatch(nativeCallIOSActions.nativeCallAccepted(event.data))
    } else if (event.name === 'RNCallKeepPerformEndCallAction') {
      dispatch(nativeCallIOSActions.nativeCallDeclined(event.data))
    } else if (event.name === 'RNCallKeepDidDisplayIncomingCall') {
      const displayEvent = parseDisplayEvent(event.data)
      if (displayEvent) {
        dispatch(nativeCallIOSActions.nativeCallDisplayed(displayEvent))
      }
    }
  }
  RNCallKeep.clearInitialEvents()

  RNCallKeep.addEventListener('answerCall', (data) =>
    dispatch(nativeCallIOSActions.nativeCallAccepted(data))
  )
  RNCallKeep.addEventListener('endCall', (data) =>
    dispatch(nativeCallIOSActions.nativeCallDeclined(data))
  )
  RNCallKeep.addEventListener('didDisplayIncomingCall', (data) => {
    const displayEvent = parseDisplayEvent(data)
    if (displayEvent) {
      dispatch(nativeCallIOSActions.nativeCallDisplayed(displayEvent))
    }
  })
}
